import Chord from './Chord';

/**
 * Classe Measure
 *
 * @author Fabien Bellanger
 */
export default class Measure
{
    /**
     * Constructeur
     *
     * @author Fabien Bellanger
     */
    constructor()
    {
        // Variables
        // ---------
        this.chords = [];
        this.openRepeat = false;
        this.closeRepeat = false;
        this.numberRepeats = 0;
        this.bgcolor = '#ffffff';
    }

    /**
     * Retourne une mesure vide
     *
     * @static
     * @author Fabien Bellanger
     * @return {Object}
     */
    static getEmptyMeasure()
    {
        return {
            chords: [
                Chord.getEmpty(),
                Chord.getEmpty(),
                Chord.getEmpty(),
                Chord.getEmpty(),
            ],
            openRepeat:    false,
            closeRepeat:   false,
            numberRepeats: 0,
            bgcolor:       '#ffffff',
        };
    }

    /**
     * La mesure est-elle valide ?
     *
     * @static
     * @author Fabien Bellanger
     * @param {Array} measure Mesure
     * @return {Boolean}
     */
    static isValid(measure)
    {
        let valid              = true;
        const nbNotEmptyChords = this.getTotalNotEmptyChordsNumber(measure);

        // Une mesure est valide si :
        //    - nombre d'élément du tableau vaut 4
        //    - si le temps 1 est toujours défini
        //    - si 2 accords, les temps 1 et 3 doivent être définis
        //    - si 3 accords, le temps 4 ne peut pas être défini si le temps ne l'est pas

        if (measure.chords.length !== 4)
        {
            return false;
        }
        
        // if (Chord.isEmpty(measure.chords[0]))
        // {
        //     return false;
        // }

        if (nbNotEmptyChords === 1 && Chord.isEmpty(measure.chords[0]))
        {
            return false;
        }

        if (nbNotEmptyChords === 2 && (Chord.isEmpty(measure.chords[0]) || Chord.isEmpty(measure.chords[2])))
        {
            return false;
        }

        if (nbNotEmptyChords === 3 && Chord.isEmpty(measure.chords[2]) && !Chord.isEmpty(measure.chords[3]))
        {
            return false;
        }

        return valid;
    }

    /**
     * Nombre d'accords à afficher en excluant les éléments null ou vides
     *
     * @static
     * @author Fabien Bellanger
     * @param {Array} measure Mesure
     * @return {Number}
     */
    static getTotalNotEmptyChordsNumber(measure)
    {
        const notNullChords = measure.chords.filter(element => Chord.getName(element)[0] !== '');

        return notNullChords.length;
    }

    /**
     * La mesure est-elle une mesure vide ?
     *
     * @static
     * @author Fabien Bellanger
     * @param {Array} measure Mesure
     * @return {Boolean}
     */
    static isEmptyMeasure(measure)
    {
        const repeatedMeasure = measure.chords.filter(element => Chord.getName(element)[0] !== '');

        return (repeatedMeasure.length === 0);
    }

    /**
     * La mesure est-elle une mesure répérée ?
     *
     * @static
     * @author Fabien Bellanger
     * @param {Array} measure Mesure
     * @return {Boolean}
     */
    static isRepeatedMeasure(measure)
    {
        const repeatedMeasure = measure.chords.filter(element => Chord.getName(element)[0] === '%');

        return (repeatedMeasure.length > 0);
    }

    /**
     * Retourne le numéro de la mesure
     *
     * @static
     * @author Fabien Bellanger
     * @param {Song}   song             Chanson
     * @param {Number} partIndex        Indice de la partie
     * @param {Number} measureIndex     Indice de la mesure dans la partie
     * @return {Number}
     */
    static getMeasureNumber(song, partIndex, measureIndex)
    {
        const partsNumber = song.parts.length;
        let number        = 0;
        let i             = 0;

        while (!(i === partsNumber || i === partIndex))
        {
            number += song.parts[i].measures.length;

            i++;
        }

        if (i < partsNumber)
        {
            number += measureIndex + 1;
        }

        return number;
    }

    /**
     * Retourne la liste des couleurs disponibles pour le fond d'une mesure
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getColorsList()
    {
        return [
            '#FFCDD2',
            '#F8BBD0',
            '#E1BEE7',
            '#D1C4E9',
            '#C5CAE9',
            '#BBDEFB',
            '#B3E5FC',
            '#B2EBF2',
            '#B2DFDB',
            '#C8E6C9',
            '#DCEDC8',
            '#F0F4C3',
            '#FFF9C4',
            '#FFECB3',
            '#FFE0B2',
            '#FFCCBC',
            '#D7CCC8',
            '#E0E0E0',
            '#CFD8DC',
            '#FFFFFF',
        ];
    }

    /**
     * Affichage du symbole de début de répétition
     *
     * @static
     * @author Fabien Bellanger
     * @param {Object} draw             SVG
     * @param {Object} group            Groupe
     * @param {Number} defaultHeight    Hauteur
     * @param {Number} strokeWidth      Bordure
     */
    static drawOpenRepeat(draw, group, defaultHeight, strokeWidth)
    {
        const line1   = draw.line(5, 0, 5, defaultHeight).stroke({ width: strokeWidth });
        const line2   = draw.line(1, 0, 1, defaultHeight).stroke({ width: strokeWidth + 2 });
        const circle1 = draw.circle(2.5).move(8, defaultHeight / 2 - defaultHeight / 7);
        const circle2 = draw.circle(2.5).move(8, 3 * defaultHeight / 4 - defaultHeight / 7);
        
        group.add(line1);
        group.add(line2);
        group.add(circle1);
        group.add(circle2);
    }

    /**
     * Affichage du symbole de fin de répétition
     *
     * @static
     * @author Fabien Bellanger
     * @param {Object} draw             SVG
     * @param {Object} group            Groupe
     * @param {Number} defaultWidth     Longueur
     * @param {Number} defaultHeight    Hauteur
     * @param {Number} strokeWidth      Bordure
     */
    static drawCloseRepeat(draw, group, defaultWidth, defaultHeight, strokeWidth)
    {
        const line1   = draw.line(defaultWidth - 4, 0, defaultWidth - 4, defaultHeight).stroke({ width: strokeWidth });
        const line2   = draw.line(defaultWidth, 0, defaultWidth, defaultHeight).stroke({ width: strokeWidth + 2 });
        const circle1 = draw.circle(2.5).move(defaultWidth - 9, defaultHeight / 2 - defaultHeight / 7);
        const circle2 = draw.circle(2.5).move(defaultWidth - 9, 3 * defaultHeight / 4 - defaultHeight / 7);
        
        group.add(line1);
        group.add(line2);
        group.add(circle1);
        group.add(circle2);
    }

    /**
     * Affichage du numéro de mesure
     *
     * @deprecated
     * @static
     * @author Fabien Bellanger
     * @param {Object} draw             SVG
     * @param {Object} song             Chanson
     * @param {Measure} measure         Mesure
     * @param {Number} measureNumber    Numéro de mesure
     * @param {Number} partIndex        Indice de la partie
     */
    static drawMeasureNumber(draw, song, measure, measureNumber, partIndex)
    {
        if (song.displayMeasureNumber && partIndex !== -1)
        {
            const xText         = (measure.openRepeat) ? 8 : 3;
            const measureNumberDraw = draw.text(measureNumber.toString()).move(xText, 8);
            
            measureNumberDraw.font({ anchor: 'left', size: 9, family: 'Roboto', fill: '#444' });
            measureNumberDraw.front();
        }
    }

    /**
     * Affichage du numbre de répétitions
     *
     * @deprecated
     * @static
     * @author Fabien Bellanger
     * @param {Object} draw             SVG
     * @param {Object} song             Chanson
     * @param {Measure} measure         Mesure
     * @param {Number} measureNumber    Numéro de mesure
     * @param {Number} partIndex        Indice de la partie
     */
    static drawNumberRepeats(draw, measure, defaultWidth, strokeWidth)
    {
        if (measure.numberRepeats > 2)
        {
            const repeatsGroup = draw.group();
            const repeats      = draw.text('x ' + measure.numberRepeats.toString()).move(defaultWidth - 30, 4);
            const rect         = draw.rect(30, 18)
                .fill('#ffffff')
                .stroke({ width: strokeWidth })
                .move(defaultWidth - 30, 0);
            repeats.font({ anchor: 'right', size: 13, family: 'Roboto', fill: '#000' });
            
            repeatsGroup.add(rect);
            repeatsGroup.add(repeats);
            repeatsGroup.front();
        }
    }
};
